/**
 * Colori e label condivisi del modulo HR.
 * Centralizza il mapping da codici interni a chip Vuetify per evitare
 * duplicazione tra Cartellino, Timeline, Note Spese, Ferie/Giustificativi.
 */

import { localDateStr } from './hrDates'

export const directionColor = (d) => ({
    in: 'green',
    out: 'red',
    break_in: 'orange',
    break_out: 'blue',
}[d] || 'grey')

export const directionText = (d) => ({
    in: 'Entrata',
    out: 'Uscita',
    break_in: 'Inizio Pausa',
    break_out: 'Fine Pausa',
}[d] || d)

export const directionIcon = (d) => ({
    in: 'mdi-login',
    out: 'mdi-logout',
    break_in: 'mdi-pause',
    break_out: 'mdi-play',
}[d] || 'mdi-circle')

export const causeText = (c) => ({
    ordinary: 'Lavoro Ordinario',
    smart_working: 'Smart Working',
    travel: 'Viaggio',
    other: 'Altro',
}[c] || c)

export const sourceText = (s) => ({
    mobile_app: 'App Mobile',
    web: 'Web',
    nfc: 'NFC',
    qrcode: 'QR Code',
    hardware: 'Hardware',
    manual: 'Manuale (HR)',
}[s] || s)

export const anomalyText = (a) => ({
    double_in: 'Doppia entrata',
    out_without_in: 'Uscita senza entrata',
    missing_out: 'Uscita mancante',
    out_of_geofence: 'Fuori sede (geofence)',
    late_in: 'Ingresso in ritardo',
    no_gps_match: 'Nessun match GPS',
    break_not_clocked: 'Pausa non timbrata',
    break_short: 'Pausa corta',
}[a] || a)

/** Anomalie di pausa: informative, gia' risolte dalla detrazione della pausa minima. */
export const BREAK_ANOMALIES = ['break_not_clocked', 'break_short']

export const isBreakAnomaly = (a) => BREAK_ANOMALIES.includes(a)

/** Etichetta dell'anomalia con i minuti di pausa detratti dal giorno (wd.minutes_break_deducted). */
export const anomalyLabel = (a, wd) => {
    const m = wd && wd.minutes_break_deducted ? Number(wd.minutes_break_deducted) : 0
    if (a === 'break_not_clocked' && m > 0) return `Pausa non timbrata: detratti ${m} min`
    if (a === 'break_short' && m > 0) return `Pausa corta: integrati ${m} min`
    return anomalyText(a)
}

export const wdStatusText = (s) => ({
    ok: 'OK',
    anomaly: 'Anomalia',
    pending_hr: 'In revisione HR',
    locked: 'Bloccato',
}[s] || s)

export const wdStatusColor = (s) => ({
    ok: 'green',
    anomaly: 'orange',
    pending_hr: 'blue',
    locked: 'grey',
}[s] || 'grey')

export const absenceStatusText = (s) => ({
    draft: 'Bozza',
    pending: 'In attesa',
    approved: 'Approvata',
    rejected: 'Rifiutata',
    cancelled: 'Annullata',
}[s] || s)

export const absenceStatusColor = (s) => ({
    draft: 'grey',
    pending: 'orange',
    approved: 'green',
    rejected: 'red',
    cancelled: 'grey',
}[s] || 'grey')

export const expenseStatusText = (s) => ({
    pending: 'In attesa',
    submitted: 'Inviata',
    approved: 'Approvata',
    rejected: 'Rifiutata',
    reimbursed: 'Rimborsata',
}[s] || s)

export const expenseStatusColor = (s) => ({
    pending: 'orange',
    submitted: 'blue',
    approved: 'green',
    rejected: 'red',
    reimbursed: 'teal',
}[s] || 'grey')

export const expenseCategoryText = (c) => ({
    meal: 'Pasto',
    travel: 'Trasferta',
    km: 'Rimborso km',
    fuel: 'Carburante',
    lodging: 'Alloggio',
    parking: 'Parcheggio/pedaggio',
    other: 'Altro',
}[c] || c)

export const formatMinutes = (m) => {
    if (m === null || m === undefined) return '—'
    const h = Math.floor(m / 60)
    const min = m % 60
    return `${h}h ${String(min).padStart(2, '0')}m`
}

// Saldo ore con segno (+/-): lavorato + giustificato - teorico.
export const formatBalance = (m) => {
    if (m === null || m === undefined) return '—'
    if (m === 0) return '0h 00m'
    return `${m > 0 ? '+' : '-'}${formatMinutes(Math.abs(m))}`
}

/** Colore del saldo con segno: verde positivo, rosso negativo, neutro a 0 o assente. */
export const balanceClass = (m) => {
    if (m > 0) return 'green--text text--darken-2'
    if (m < 0) return 'red--text text--darken-2'
    return ''
}

// Periodo di compensazione (settimana/mese) aperto all'ultima compilazione:
// saldo provvisorio, straordinario non ancora assegnato. Righe compilate prima
// (period_open null) non danno indicazioni.
export const isPeriodOpen = (wd) => !!wd && (wd.period_open === true || wd.period_open === 1 || wd.period_open === '1')

// Fine del periodo come gg/mm, dalla data Y-m-d del backend (senza fusi).
export const periodEndText = (wd) => wd && wd.period_end
    ? `${String(wd.period_end).substring(8, 10)}/${String(wd.period_end).substring(5, 7)}`
    : ''

// Periodo segnato aperto ma gia' finito: manca la ricompilazione dopo la fine
// del periodo (job notturno saltato o fallito, ambienti senza scheduler).
export const isPeriodStale = (wd) => isPeriodOpen(wd) && !!wd.period_end && String(wd.period_end).substring(0, 10) < localDateStr()

export const STALE_PERIOD_TEXT = 'Periodo concluso: ricompila il cartellino per assegnare lo straordinario'

const periodNoun = (wd) => wd && wd.period_scope === 'monthly' ? 'mese' : 'settimana'

export const pendingOvertimeText = (wd) => isPeriodStale(wd)
    ? STALE_PERIOD_TEXT
    : `In attesa di fine ${periodNoun(wd)}` + (periodEndText(wd) ? ` (${periodEndText(wd)})` : '')

export const provisionalBalanceText = (wd) => isPeriodStale(wd)
    ? STALE_PERIOD_TEXT
    : `Saldo provvisorio: ${periodNoun(wd)} in corso` + (periodEndText(wd) ? ` fino al ${periodEndText(wd)}` : '')

export default {
    directionColor, directionText, directionIcon,
    causeText, sourceText, anomalyText, anomalyLabel, isBreakAnomaly,
    wdStatusText, wdStatusColor,
    absenceStatusText, absenceStatusColor,
    expenseStatusText, expenseStatusColor, expenseCategoryText,
    formatMinutes, formatBalance, balanceClass,
    isPeriodOpen, isPeriodStale, periodEndText, pendingOvertimeText, provisionalBalanceText,
}
